import { useState } from 'react'
import { useSelector } from 'react-redux'

import InviteResultsPanel, { ResultRow } from './InviteResultsPanel'
import { resendableInviteIds, resendInviteOptions, resendResultRows, wasResent } from './memberTableUtils'

import styles from './ResendAllInvites.module.scss'

import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import MemberMultiSelect from '@/components/MemberMultiSelect'
import { useToast } from '@/components/Toast/ToastContext'
import { useTeamMemberActions } from '@/hooks/useTeamMemberActions'
import { selectInvites } from '@/store/features/member/memberSlice'
import { selectIsAdmin } from '@/store/features/user/userSlice'

const pluralizeInvites = (count: number) => `${count} invite${count === 1 ? '' : 's'}`

/** "Resend pending invites (N)": pick which pending or failed invites to resend, then show the results. */
const ResendAllInvites = ({ isScimEnabled }: { isScimEnabled: boolean }) => {
  const canResend = useSelector(selectIsAdmin) && !isScimEnabled
  const invites = useSelector(selectInvites)
  const { resendInvites } = useTeamMemberActions()
  const { updateToast } = useToast()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isResending, setIsResending] = useState(false)
  const [resultRows, setResultRows] = useState<ResultRow[] | null>(null)

  const ids = resendableInviteIds(invites)
  // A refetch while the picker is open can drop invites; only send ones still resendable.
  const idsToSend = selectedIds.filter((id) => ids.includes(id))

  if (!canResend) return null

  const openPicker = () => {
    setSelectedIds(ids)
    setIsPickerOpen(true)
  }

  const resendSelected = async () => {
    if (isResending || idsToSend.length === 0) return
    setIsResending(true)
    setResultRows(null)
    // The refetch after the call can drop rows the panel still needs to name.
    const invitesAtSend = invites
    const results = await resendInvites(idsToSend)
    setIsResending(false)
    setIsPickerOpen(false)
    if (!results) return
    if (results.every(wasResent)) {
      updateToast(`Resent ${pluralizeInvites(results.length)}`, 'success')
    } else {
      setResultRows(resendResultRows(results, invitesAtSend))
    }
  }

  return (
    <>
      {ids.length > 0 && (
        <Button
          type="light"
          title={`Resend pending invites (${ids.length})`}
          onClick={openPicker}
          isDisabled={isResending}
          className={styles.button}
        />
      )}
      <ConfirmDialog
        isOpen={isPickerOpen}
        title="Resend invites"
        message={
          <MemberMultiSelect
            members={resendInviteOptions(invites)}
            selectedUserIds={idsToSend}
            onChange={setSelectedIds}
            isDisabled={isResending}
            showSelectAll
            searchPlaceholder="Search invites"
          />
        }
        confirmLabel={idsToSend.length === 0 ? 'Resend' : `Resend ${pluralizeInvites(idsToSend.length)}`}
        confirmType="dark"
        isConfirming={isResending}
        isConfirmDisabled={idsToSend.length === 0}
        onConfirm={() => void resendSelected()}
        onCancel={() => setIsPickerOpen(false)}
      />
      {resultRows && (
        <div className={styles.results}>
          <InviteResultsPanel
            rows={resultRows}
            onDismiss={() => setResultRows(null)}
            dismissLabel="Dismiss resend results"
          />
        </div>
      )}
    </>
  )
}

export default ResendAllInvites
